/**
 * THE floating option menu of the editor — one visual for every popup list
 * (the ⊕ add-edge-item menu over the 2D plan, the + add-element menu on the
 * stage). White card, ink hairline, plain clickable rows; the React Native
 * counterpart is kaiPro's OptionMenu.
 */

import { css } from '@emotion/react';
import { accentSoft, bone, focusRing, ink, menuShadow, midtone, white } from '../styles';
import { usePopupOpenState } from './usePopupOpenState';

export const OPTION_MENU_WIDTH = 152;
export const OPTION_MENU_ROW_HEIGHT = 31;
export const OPTION_MENU_PADDING = 4;

/** The menu's rendered height for the given option count (position clamping). */
export const optionMenuHeight = (optionCount: number): number => 2 * OPTION_MENU_PADDING + optionCount * OPTION_MENU_ROW_HEIGHT;

const OPTION_MENU_STYLE = css`
  label: OPTION_MENU_STYLE;
  width: ${OPTION_MENU_WIDTH}px; box-sizing: border-box;
  background: ${white}; border: 1px solid ${ink};
  box-shadow: ${menuShadow}; padding: ${OPTION_MENU_PADDING}px;
  display: flex; flex-direction: column; gap: 1px; pointer-events: auto; z-index: 20;
`;

const OPTION_STYLE = css`
  label: OPTION_STYLE;
  text-align: left; padding: 7px 14px; border: none; background: none;
  font: inherit; font-size: 13px; color: ${ink}; cursor: pointer; white-space: nowrap;
  &:hover { background: ${accentSoft}; color: ${ink}; }
`;

export function OptionMenu({ options, onSelect, style, testId }: {
  /** [key, label] rows, in display order. */
  options: [string, string][];
  onSelect: (key: string) => void;
  /** Positioning (typically absolute placement from the host). */
  style?: React.CSSProperties;
  testId?: string;
}): React.JSX.Element {
  return (
    <div css={OPTION_MENU_STYLE} data-testid={testId} style={style}>
      {options.map(([key, label]) => (
        <button css={OPTION_STYLE} key={key} onClick={() => onSelect(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

const SELECT_HOST_STYLE = css`
  label: SELECT_HOST_STYLE;
  position: relative; display: inline-flex; min-width: 0;
`;

/**
 * The trigger: one fixed width for every pick list (kaiPro's selectListStyle
 * counterpart), label left / solid down caret right, one font size smaller
 * than the panel body (matching the menu rows).
 */
const SELECT_TRIGGER_STYLE = css`
  label: SELECT_TRIGGER_STYLE;
  display: inline-flex; align-items: center; justify-content: space-between; gap: 6px;
  box-sizing: border-box; width: 158px;
  padding: 6px 8px; border: 1px solid ${bone};
  font: inherit; font-size: 13px; background: ${white}; color: ${ink};
  cursor: pointer;
  &:focus {
    outline: none; border-color: ${ink};
    box-shadow: ${focusRing};
  }
  &:disabled { color: ${midtone}; cursor: default; }
`;

const SELECT_TRIGGER_LABEL_STYLE = css`
  label: SELECT_TRIGGER_LABEL_STYLE;
  min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

/** Solid down triangle, right-aligned in the trigger. */
function SelectCaretIcon(): React.JSX.Element {
  return (
    <svg width={10} height={6} viewBox='0 0 10 6' aria-hidden style={{ flexShrink: 0 }}>
      <polygon points='0,0 10,0 5,6' fill={ink} />
    </svg>
  );
}

/**
 * A select-style trigger that opens the OptionMenu popup: the editor's
 * dropdown pick list (same popup visual as the ⊕ add-edge-item and + add-
 * element menus). The selected option's label shows on the trigger, the
 * placeholder while none is selected.
 */
export function OptionMenuSelect({ id, ariaLabel, options, value, placeholder, disabled = false, onSelect }: {
  /** Stable E2E hook on the trigger button. */
  id?: string;
  ariaLabel?: string;
  /** [key, label] rows, in display order. */
  options: [string, string][];
  /** The selected option's KEY; undefined when none is selected. */
  value: string | undefined;
  /** What the trigger shows while no option is selected. */
  placeholder?: string;
  /** A disabled trigger never opens. */
  disabled?: boolean;
  onSelect: (key: string) => void;
}): React.JSX.Element {
  const { open, setOpen, hostRef } = usePopupOpenState();
  return (
    <div css={SELECT_HOST_STYLE} ref={hostRef}>
      <button
        id={id}
        css={SELECT_TRIGGER_STYLE}
        aria-label={ariaLabel}
        aria-haspopup='listbox'
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <span css={SELECT_TRIGGER_LABEL_STYLE}>{options.find(([key]) => key === value)?.[1] ?? placeholder}</span>
        <SelectCaretIcon />
      </button>
      {open && (
        <OptionMenu
          options={options}
          onSelect={key => {
            setOpen(false);
            onSelect(key);
          }}
          style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)' }}
        />
      )}
    </div>
  );
}
